import { mapGetters } from 'vuex';
import { formatResponseError } from '../../utils/api';
import api from '../../api';
import AssignIcon from '../policy/meta-data-manager/assign_icon.svg';
import UnAssignIcon from '../policy/meta-data-manager/unassign_icon.svg';
import assignActionCellRenderer from './cell-renderers/assign-action-cell-renderer.vue';

export default {
    components: { assignActionCellRenderer },
    data() {
        return {
            isAssigned: true,
            selectedRow: '',
            modalId: '',
            initialValues: null,
            rowData: [],
            properties: [],
            windowWidth: 0,
            windowHeight: 0,
            busy: false,
        };
    },
    mounted() {
        this.onResize();
        window.addEventListener('resize', this.onResize);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.onResize);
    },
    computed: {
        ...mapGetters('header', ['userIsSubscriber']),
        isMobile() {
            const md = 768;
            return this.windowWidth < md || this.windowHeight < md;
        },
        truncatedTitle() {
            return this.selectedRow.title.length > (this.isMobile ? 5 : 30)
                ? this.selectedRow.title.slice(0, this.isMobile ? 5 : 30) + '...'
                : this.selectedRow.title;
        },
        filterTitle() {
            return `"${this.truncatedTitle}" is ${this.isAssigned ? 'assigned' : 'not assigned'}`;
        },
        filterIcon() {
            return !this.isAssigned
                ? `<img class="metadata-assign-icon" src="${AssignIcon}"></img>`
                : `<img class="metadata-assign-icon" src="${UnAssignIcon}"></img>`;
        },
        propertiesColumnDefs() {
            return [
                {
                    field: 'id',
                    headerName: 'ID',
                },
                {
                    field: 'name',
                    headerName: 'Name',
                    autoWidthExclude: true,
                    minWidth: 200,
                },
                {
                    field: '',
                    headerName: 'Bedrooms/Baths',
                    cellRenderer: ({ data }) => `${data.bedrooms} / ${data.baths}`,
                    minWidth: 150,
                },
                {
                    field: '',
                    cellRenderer: 'assignActionCellRenderer',
                    cellRendererParams: {
                        isAssign: this.isAssigned,
                        onClick: (data) => this.assignProperty(data),
                        propertyName: this.propertyName,
                    },
                    maxWidth: 200,
                    minWidth: 200,
                },
            ];
        },
        propertiesFilterOptions() {
            return [
                {
                    label: 'Assigned',
                    click: () => {
                        this.isAssigned = true;
                        this.fetchProperties();
                    },
                    customHTML: `<img class="metadata-assign-icon" src="${AssignIcon}"></img>`,
                    dropDownItemClass: 'filter-by-status-dropdown-item',
                },
                {
                    label: 'Not Assigned',
                    click: () => {
                        this.isAssigned = false;
                        this.fetchProperties();
                    },
                    customHTML: `<img class="metadata-assign-icon" src="${UnAssignIcon}"></img>`,
                    dropDownItemClass: 'filter-by-status-dropdown-item',
                },
            ];
        },
    },
    methods: {
        onResize() {
            this.windowWidth = window.innerWidth;
            this.windowHeight = window.innerHeight;
        },
        async fetchProperties() {
            try {
                const { data } = await api.get(this.endpoint(this.selectedRow.id) + 'properties/', {
                    params: {
                        is_assigned: this.isAssigned,
                    },
                });
                this.properties = data;
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not fetch properties' });
            }
        },
        handleRowSelect() {
            const gridApi = this.$refs.grid.gridOptions.api;
            const selectedRows = gridApi.getSelectedRows();
            this.selectedRow = selectedRows.length ? selectedRows[0] : null;
            if (this.selectedRow) {
                this.fetchProperties();
            }
        },
        async fetchRowData() {
            try {
                this.$parent.$emit('handleBusy', true);
                const { data } = await api.get(
                    this.endpoint(),
                    this.params
                        ? {
                              params: this.params,
                          }
                        : null
                );
                this.rowData = data;
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not fetch records' });
            } finally {
                this.$parent.$emit('handleBusy', false);
            }
        },
        initialRowSelect(params) {
            if (this.selectedRow) return;
            if (params?.api) {
                const gridApi = params.api;
                gridApi.forEachNodeAfterFilterAndSort((node, i) => i === 0 && node.setSelected(true));
            }
        },
        async bulkAssignToProperty(isAssign) {
            try {
                this.$parent.$emit('handleBusy', true);
                await api.post(`${this.endpoint()}toggle-assign-all-properties/`, {
                    id: this.selectedRow.id,
                    is_assign: isAssign,
                });
                await this.fetchProperties();
                this.successToast({
                    text: `Successfully ${isAssign ? 'assigned' : 'removed'} records ${
                        isAssign ? 'to' : 'from'
                    } property`,
                });
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({
                    text: generic || `Could not ${isAssign ? 'assign' : 'remove'} records from property`,
                });
            } finally {
                this.$parent.$emit('handleBusy', false);
            }
        },
        async assignProperty(data) {
            try {
                this.$parent.$emit('handleBusy', true);
                await api.post(`${this.endpoint()}toggle-assign-property/`, {
                    id: this.selectedRow.id,
                    rental_property_id: data.id,
                    is_assign: !this.isAssigned,
                });
                await this.fetchProperties();
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({
                    text: generic || `Could not ${this.isAssigned ? 'remove' : 'add'} record from property`,
                });
            } finally {
                this.$parent.$emit('handleBusy', false);
            }
        },
        async onAddEditRecord(values) {
            try {
                this.$parent.$emit('handleBusy', true);
                this.busy = true;
                if (values.id) {
                    await api.patch(this.endpoint(values.id), values);
                } else {
                    await api.post(this.endpoint(), values);
                }
                this.$bvModal.hide(this.modalId);
                this.initialValues = null;
                this.modalId = '';
                await this.fetchRowData();
                const gridApi = this.$refs.grid.gridOptions.api;
                if (values.id) {
                    gridApi.forEachNodeAfterFilter((node, i) => {
                        return Number(node.id) === Number(values.id) && node.setSelected(true);
                    });
                } else {
                    gridApi.forEachNodeAfterFilter((node, i) => {
                        return i === 0 && node.setSelected(true);
                    });
                }
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({
                    text: values.id ? generic || 'Could not edit record' : generic || 'Could not add record',
                });
            } finally {
                this.busy = false;
                this.$parent.$emit('handleBusy', false);
            }
        },
    },
};
